// Semantic trigger search - maps natural language to trigger shortcuts
// Helps beginners discover triggers by typing what they want

import { allMathTriggers } from "../triggers";
import type { TriggerDefinition } from "../triggers";

// Additional keywords that map to triggers (beyond the name field)
const triggerKeywords: Record<string, string[]> = {
    // Greek letters
    ".a": ["alpha", "α"],
    ".b": ["beta", "β"],
    ".c": ["chi", "χ"],
    ".d": ["delta", "δ"],
    ".e": ["epsilon", "ε"],
    ".g": ["gamma", "γ"],
    ".h": ["eta", "η"],
    ".i": ["iota", "ι"],
    ".k": ["kappa", "κ"],
    ".l": ["lambda", "λ"],
    ".m": ["mu", "μ"],
    ".n": ["nu", "ν"],
    ".o": ["omega", "ω"],
    ".p": ["pi", "π"],
    ".r": ["rho", "ρ"],
    ".s": ["sigma", "σ"],
    ".t": ["tau", "τ"],
    ".u": ["upsilon", "υ"],
    ".x": ["xi", "ξ"],
    ".z": ["zeta", "ζ"],
    ".ph": ["phi", "φ"],
    ".ps": ["psi", "ψ"],
    ".th": ["theta", "θ"],
    ".D": ["Delta", "Δ", "big delta"],
    ".G": ["Gamma", "Γ", "big gamma"],
    ".L": ["Lambda", "Λ", "big lambda"],
    ".O": ["Omega", "Ω", "big omega"],
    ".Ph": ["Phi", "Φ", "big phi"],
    ".Ps": ["Psi", "Ψ", "big psi"],
    ".Th": ["Theta", "Θ", "big theta"],
    ".S": ["Sigma", "Σ", "big sigma", "sum symbol"],
    ".Pi": ["Pi", "Π", "big pi", "product symbol"],

    // Fractions
    "ff": ["fraction", "frac", "divide", "over", "ratio"],
    "cf": ["continued fraction", "cfrac"],
    "bm": ["binomial", "choose", "combination", "nCr"],

    // Delimiters / Brackets
    "dp": ["parentheses", "parens", "round brackets", "()", "paren"],
    "ds": ["square brackets", "brackets", "[]", "bracket"],
    "bb": ["braces", "curly", "curly brackets", "{}", "brace"],
    "db": ["extensible braces", "big braces", "auto braces"],
    "dk": ["angle brackets", "chevrons", "<>", "angle"],
    "da": ["absolute value", "pipes", "abs", "|x|", "modulus"],
    "dn": ["norm", "double pipes", "||x||", "magnitude"],
    "ce": ["ceiling", "ceil", "round up", "⌈⌉"],
    "fl": ["floor", "round down", "⌊⌋"],

    // Subscript/Superscript
    ";": ["subscript", "sub", "index", "lower"],
    ":": ["superscript", "sup", "power", "exponent", "upper"],
    "bs": ["both", "sub and sup", "subscript superscript"],

    // Roots
    "sq": ["square root", "sqrt", "root", "√"],
    "nr": ["nth root", "cube root", "radical"],

    // Operators
    "pm": ["plus minus", "±", "plusminus"],
    "mp": ["minus plus", "∓"],
    "tm": ["times", "×", "multiply", "cross"],
    "cd": ["dot", "·", "centered dot", "multiply"],
    "opl": ["oplus", "⊕", "direct sum", "xor"],
    "omt": ["otimes", "⊗", "tensor", "tensor product"],

    // Relations
    "le": ["less than or equal", "<=", "≤", "leq"],
    "ge": ["greater than or equal", ">=", "≥", "geq"],
    "ne": ["not equal", "!=", "≠", "neq"],
    "app": ["approximately", "≈", "approx", "about"],
    "eq": ["equivalent", "≡", "congruent", "equiv"],

    // Set theory
    "in": ["element of", "∈", "belongs to", "member"],
    "ntn": ["not in", "∉", "not element"],
    "sbs": ["subset", "⊂", "contained in"],
    "sbq": ["subset equal", "⊆", "subseteq"],
    "nun": ["union", "∪", "cup"],
    "nit": ["intersection", "∩", "cap"],
    "vc": ["empty set", "∅", "null set", "void"],

    // Logic
    "fa": ["for all", "∀", "forall", "universal"],
    "ex": ["exists", "∃", "there exists", "existential"],
    "nx": ["not exists", "∄", "nexists"],
    "lan": ["and", "∧", "land", "logical and", "conjunction"],
    "lor": ["or", "∨", "logical or", "disjunction"],
    "lt": ["not", "¬", "negation", "lnot"],
    "ip": ["implies", "⇒", "then", "implication", "arrow"],
    "iff": ["if and only if", "⇔", "iff", "biconditional", "equivalent"],

    // Calculus
    "ii": ["integral", "∫", "integrate", "int"],
    "itn": ["integral", "∫"],
    "itd": ["definite integral", "integral from to"],
    "der": ["derivative", "d/dx", "differentiate"],
    "pdr": ["partial derivative", "∂", "partial"],
    "nb": ["nabla", "∇", "gradient", "del"],
    "lm": ["limit", "lim"],

    // Sums and products
    "sm": ["sum", "Σ", "summation"],
    "ss": ["sum from to", "definite sum"],
    "pp": ["product", "Π", "prod"],

    // Functions
    "sin": ["sine"],
    "cos": ["cosine"],
    "tan": ["tangent"],
    "cot": ["cotangent"],
    "sec": ["secant"],
    "csc": ["cosecant"],
    "ln": ["natural log", "logarithm", "log base e"],
    "lg": ["log", "logarithm"],
    "xp": ["exponential", "exp", "e^x"],

    // Arrows
    "rar": ["right arrow", "→", "rightarrow", "to"],
    "lar": ["left arrow", "←", "leftarrow", "from"],
    "lra": ["left right arrow", "↔", "leftrightarrow"],
    "Rar": ["implies arrow", "⇒", "Rightarrow"],
    "Lar": ["implied by", "⇐", "Leftarrow"],

    // Accents
    "ht": ["hat", "^", "circumflex"],
    "til": ["tilde", "~", "wave"],
    "vv": ["vector", "→", "arrow over"],
    "dr": ["dot", "derivative dot", "time derivative"],
    "dd": ["double dot", "acceleration"],
    "br": ["bar", "overline", "average", "mean"],
    "ovl": ["overline", "bar", "average"],

    // Matrices
    "p2x2": ["2x2 matrix", "two by two"],
    "p3x3": ["3x3 matrix", "three by three"],
    "b2x2": ["2x2 bracket matrix"],
    "pgn": ["generic matrix", "general matrix", "n by m"],

    // Quantum mechanics
    "ba": ["bra", "⟨|", "dual vector"],
    "kt": ["ket", "|⟩", "state vector"],
    "bk": ["braket", "⟨|⟩", "inner product"],

    // Cases
    "cs": ["cases", "piecewise", "piecewise function", "conditional", "begin cases"],

    // Misc
    "fy": ["infinity", "∞", "inf"],
    "ll": ["ell", "ℓ", "script l"],
    "bx": ["boxed", "box", "highlight"],
    "tx": ["text", "words", "roman text"],

    // Math fonts
    "mc": ["calligraphic", "curly", "script"],
    "mb": ["bold", "boldface"],
    "mk": ["blackboard bold", "double struck", "ℝ", "ℤ", "ℕ", "real numbers", "integers", "naturals"],
    "mf": ["fraktur", "gothic"],
};

export interface TriggerSuggestion {
    trigger: TriggerDefinition;
    score: number;
    matchedOn: string; // What the user typed that matched
}

/**
 * Search for triggers that match the user's natural language query.
 * Returns suggestions sorted by relevance.
 */
export function searchTriggers(query: string, limit: number = 6): TriggerSuggestion[] {
    if (!query || query.length < 2) return [];

    const normalizedQuery = query.toLowerCase().trim();
    const suggestions: TriggerSuggestion[] = [];

    for (const trigger of allMathTriggers) {
        // Skip regex-only triggers
        if (trigger.isRegex && !trigger.trigger) continue;

        let bestScore = 0;
        let matchedOn = "";

        // Check trigger name
        const nameMatch = fuzzyMatch(normalizedQuery, trigger.name.toLowerCase());
        if (nameMatch > bestScore) {
            bestScore = nameMatch;
            matchedOn = trigger.name;
        }

        // Check trigger code itself
        const triggerMatch = fuzzyMatch(normalizedQuery, trigger.trigger.toLowerCase());
        if (triggerMatch > bestScore) {
            bestScore = triggerMatch;
            matchedOn = trigger.trigger;
        }

        // Check additional keywords
        const keywords = triggerKeywords[trigger.trigger];
        if (keywords) {
            for (const keyword of keywords) {
                const keywordMatch = fuzzyMatch(normalizedQuery, keyword.toLowerCase());
                if (keywordMatch > bestScore) {
                    bestScore = keywordMatch;
                    matchedOn = keyword;
                }
            }
        }

        // Check aliases from the trigger definition itself
        if (trigger.aliases) {
            for (const alias of trigger.aliases) {
                const aliasMatch = fuzzyMatch(normalizedQuery, alias.toLowerCase());
                if (aliasMatch > bestScore) {
                    bestScore = aliasMatch;
                    matchedOn = alias;
                }
            }
        }

        // Check category
        const categoryMatch = fuzzyMatch(normalizedQuery, trigger.category.toLowerCase());
        if (categoryMatch > bestScore * 0.8) { // Category match is weighted lower
            bestScore = Math.max(bestScore, categoryMatch * 0.8);
            if (matchedOn === "") matchedOn = trigger.category;
        }

        if (bestScore > 0.3) { // Threshold for inclusion
            suggestions.push({
                trigger,
                score: bestScore,
                matchedOn,
            });
        }
    }

    // Sort by score descending
    suggestions.sort((a, b) => b.score - a.score);

    return suggestions.slice(0, limit);
}

/**
 * Simple fuzzy matching algorithm.
 * Returns a score between 0 and 1.
 */
function fuzzyMatch(query: string, target: string): number {
    if (query === target) return 1;
    if (target.startsWith(query)) return 0.9;
    if (target.includes(query)) return 0.7;

    // Check if all query characters appear in order in target
    let queryIdx = 0;
    let consecutiveBonus = 0;
    let lastMatchIdx = -2;

    for (let i = 0; i < target.length && queryIdx < query.length; i++) {
        if (target[i] === query[queryIdx]) {
            if (i === lastMatchIdx + 1) {
                consecutiveBonus += 0.1;
            }
            lastMatchIdx = i;
            queryIdx++;
        }
    }

    if (queryIdx === query.length) {
        // All characters matched
        const baseScore = query.length / target.length;
        return Math.min(0.6 + consecutiveBonus, 0.85) * baseScore + 0.15;
    }

    return 0;
}

/**
 * Get preview LaTeX for display in hints
 */
export function getHintPreview(trigger: TriggerDefinition): string {
    let preview = trigger.latex
        .replace(/#0/g, "□")
        .replace(/#1/g, "□")
        .replace(/#2/g, "□")
        .replace(/#\d+/g, "□")
        .replace(/#\?/g, "□");

    // Truncate if too long
    if (preview.length > 30) {
        preview = preview.substring(0, 27) + "...";
    }

    return preview;
}
